import { computed, type ComputedRef } from 'vue'
import { storeToRefs } from 'pinia'
import { useWikiPagesStore } from '../stores/wiki_pages'
import { useThreadsStore } from '../stores/threads'
import { useSessionStore } from '../stores/session'
import { toggleDisplayDensity } from './useDisplayDensity'
import type { PaletteItem } from '../views/CommandPalette.types'

// Snapshot of store-derived state that buildPaletteItems needs.
// Plain interface — no store imports, fully constructable from literals
// in tests.
export interface PaletteState {
  isAdmin: boolean
  displayDensity: string | null
  pages: Array<{ id: number; path: string; title: string | null }>
  threads: Array<{ id: number; title: string }>
}

// Action surface the palette renders alongside pages and threads. The
// host view passes its own implementations so the composable doesn't
// need to know about routing, drawer state, session — that knowledge
// stays in RouteShell where it belongs.
export interface PaletteHandlers {
  openAddToWiki: () => void
  openThreads: () => void
  openDispatchTask: () => void
  goToAgent: () => void
  goToOutputSkills: () => void
  goToCalendar: () => void
  goToAgentRepos: () => void
  goToAgentPrompts: () => void
  goToProductOwnerRuns: () => void
  openThread: (id: number) => void
  navigateToPage: (id: number) => void
  newPage: () => void
  goToLog: () => void
  goHome: () => void | Promise<void>
  signOut: () => void | Promise<void>
}

// Backward-compat alias — callers importing CommandPaletteHandlers don't
// need updating.
export type CommandPaletteHandlers = PaletteHandlers

// Pure function: no store imports, no Vue reactivity. Takes a POJO
// state snapshot and the host-supplied action callbacks; returns the
// ordered item list. Fully testable with plain objects.
//
// Admin-gated entries are filtered out for non-admin sessions to mirror
// the WikiHeader visibility rules — clicking a hidden action would
// silently 403, which is worse UX than not exposing it. Ask is
// universal: non-admin users get one-shot answers from the same drawer.
export function buildPaletteItems(state: PaletteState, handlers: PaletteHandlers): PaletteItem[] {
  const items: PaletteItem[] = []

  if (state.isAdmin) {
    items.push({
      id: 'action:add',
      label: 'Add to brain',
      hint: 'Open ingest drawer',
      group: 'Actions',
      run: handlers.openAddToWiki,
    })
  }
  items.push({ id: 'action:ask', label: 'Ask', hint: 'Open Q&A drawer', group: 'Actions', run: handlers.openThreads })
  if (state.isAdmin) {
    items.push({
      id: 'action:new',
      label: 'New page',
      hint: 'Author a markdown page',
      group: 'Actions',
      run: handlers.newPage,
    })
    items.push({
      id: 'action:dispatch',
      label: 'Dispatch task',
      hint: 'Fire a Claude Code session',
      group: 'Actions',
      run: handlers.openDispatchTask,
    })
    items.push({
      id: 'action:agent',
      label: 'Agent',
      hint: 'Cloud agent sessions',
      group: 'Actions',
      run: handlers.goToAgent,
    })
    items.push({
      id: 'action:output-skills',
      label: 'Output skills',
      hint: 'Memory-grounded drafts',
      group: 'Actions',
      run: handlers.goToOutputSkills,
    })
    items.push({
      id: 'action:calendar',
      label: 'Calendar credentials',
      hint: 'Manage heartbeat OAuth',
      group: 'Actions',
      run: handlers.goToCalendar,
    })
    items.push({
      id: 'action:agent-repos',
      label: 'Agent repos',
      hint: 'Manage cloud-agent repo registry',
      group: 'Actions',
      run: handlers.goToAgentRepos,
    })
    items.push({
      id: 'action:agent-prompts',
      label: 'Prompt library',
      hint: 'Manage canned agent prompts',
      group: 'Actions',
      run: handlers.goToAgentPrompts,
    })
    items.push({
      id: 'action:product-owner-runs',
      label: 'Product-owner activity',
      hint: 'Autonomous agent run history',
      group: 'Actions',
      run: handlers.goToProductOwnerRuns,
    })
  }
  items.push({
    id: 'action:log',
    label: 'Open log',
    hint: 'Brain activity log',
    group: 'Actions',
    run: handlers.goToLog,
  })
  items.push({
    id: 'action:home',
    label: 'Home',
    hint: 'Back to brain root',
    group: 'Actions',
    run: () => {
      void handlers.goHome()
    },
  })
  // Density toggle's hint reads as the current mode so the user knows
  // what tapping does without having to remember which mode they're in.
  {
    const isDense = state.displayDensity === 'dense'
    items.push({
      id: 'action:density',
      label: isDense ? 'Switch to comfortable' : 'Switch to dense',
      hint: 'Display density',
      group: 'Actions',
      run: () => {
        void toggleDisplayDensity()
      },
    })
  }
  items.push({
    id: 'action:signout',
    label: 'Sign out',
    group: 'Actions',
    run: () => {
      void handlers.signOut()
    },
  })

  for (const page of state.pages) {
    items.push({
      id: `page:${page.id}`,
      label: page.title || page.path,
      hint: page.path,
      group: 'Pages',
      run: () => handlers.navigateToPage(page.id),
    })
  }

  for (const thread of state.threads) {
    items.push({
      id: `thread:${thread.id}`,
      label: thread.title,
      hint: 'Resume thread',
      group: 'Threads',
      run: () => handlers.openThread(thread.id),
    })
  }

  return items
}

// Thin store adapter: reads the five stores, extracts a POJO snapshot,
// and delegates item construction to the pure buildPaletteItems.
export function useCommandPaletteItems(handlers: PaletteHandlers): ComputedRef<PaletteItem[]> {
  const { pages } = storeToRefs(useWikiPagesStore())
  const { recent: recentThreads } = storeToRefs(useThreadsStore())
  const { currentUser } = storeToRefs(useSessionStore())

  return computed<PaletteItem[]>(() =>
    buildPaletteItems(
      {
        isAdmin: currentUser.value?.admin === true,
        displayDensity: currentUser.value?.display_density ?? null,
        pages: pages.value,
        threads: recentThreads.value,
      },
      handlers,
    ),
  )
}
